import { useState, useEffect } from 'react'
import AdminHeader from '../../components/admin/AdminHeader'
import AdminSidebar from '../../components/admin/AdminSidebar'
import RichTextEditor from '../../components/admin/RichTextEditor'

interface PrivacySection {
  id?: number
  section: string
  title: string
  content: string
  is_active?: number
  created_at?: string
  updated_at?: string
}

const PrivacyPolicyManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sections, setSections] = useState<PrivacySection[]>([])
  const [loading, setLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewingSection, setViewingSection] = useState<PrivacySection | null>(null)
  const [editingSection, setEditingSection] = useState<PrivacySection | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [saving, setSaving] = useState(false)

  const [formData, setFormData] = useState<PrivacySection>({
    section: '',
    title: '',
    content: '',
    is_active: 1
  })

  const API_BASE = 'https://lmtseva.com/api'

  // Fetch all sections
  const fetchSections = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE}/privacy_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Handle both array and object response formats
        if (Array.isArray(result.data)) {
          setSections(result.data)
        } else {
          // Convert object to array
          const sectionsArray = Object.values(result.data) as PrivacySection[]
          setSections(sectionsArray)
        }
      } else {
        setError(result.message || 'Failed to fetch sections')
      }
    } catch (err) {
      setError('Failed to fetch sections. Please try again.')
      console.error('Error fetching privacy sections:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSections()
  }, [])

  // Handle create/update
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.section || !formData.title || !formData.content) {
      setError('Section, title, and content are required')
      return
    }

    setError('')
    setSuccess('')
    setSaving(true)

    try {
      const payload = {
        ...(formData.id && { id: formData.id }),
        section: formData.section,
        title: formData.title,
        content: formData.content,
        is_active: formData.is_active || 1
      }

      const response = await fetch(`${API_BASE}/privacy_update.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      const result = await response.json()

      if (result.status) {
        setSuccess(formData.id ? 'Section updated successfully!' : 'Section created successfully!')
        setShowAddModal(false)
        setShowEditModal(false)
        setEditingSection(null)
        resetForm()
        fetchSections()
        
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to save section')
      }
    } catch (err) {
      setError('Failed to save section. Please try again.')
      console.error('Error saving privacy section:', err)
    } finally {
      setSaving(false)
    }
  }

  // Handle delete
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this section? This action cannot be undone.')) {
      return
    }

    setError('')
    setSuccess('')

    try {
      const response = await fetch(`${API_BASE}/privacy_delete.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Section deleted successfully!')
        fetchSections()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to delete section')
      }
    } catch (err) {
      setError('Failed to delete section. Please try again.')
      console.error('Error deleting privacy section:', err)
    }
  }

  // Open add modal
  const openAddModal = () => {
    resetForm()
    setShowAddModal(true)
  }

  // Open edit modal
  const openEditModal = (section: PrivacySection) => {
    setEditingSection(section)
    setFormData({
      id: section.id,
      section: section.section,
      title: section.title,
      content: section.content,
      is_active: section.is_active || 1
    })
    setShowEditModal(true)
  }

  // Open view modal
  const openViewModal = (section: PrivacySection) => {
    setViewingSection(section)
    setShowViewModal(true)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      section: '',
      title: '',
      content: '',
      is_active: 1
    })
    setError('')
  }

  // Filter sections
  const activeSections = sections.filter(s => (s.is_active ?? 1) === 1)
  const inactiveSections = sections.filter(s => (s.is_active ?? 1) === 0)

  return (
    <div className="min-h-screen bg-gray-900">
      <AdminHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex">
        <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 md:ml-64 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-white">Privacy Policy Management</h1>
              <button
                onClick={openAddModal}
                className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add New Section</span>
              </button>
            </div>

            {/* Success/Error Messages */}
            {success && (
              <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg">
                <p className="text-green-400">{success}</p>
              </div>
            )}
            {error && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-400 text-sm mb-1">Total Sections</p>
                <p className="text-3xl font-bold text-white">{sections.length}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-400 text-sm mb-1">Active Sections</p>
                <p className="text-3xl font-bold text-green-400">{activeSections.length}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-400 text-sm mb-1">Inactive Sections</p>
                <p className="text-3xl font-bold text-gray-400">{inactiveSections.length}</p>
              </div>
            </div>

            {/* Sections List */}
            {loading ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light mx-auto mb-4"></div>
                <p className="text-gray-400 text-lg">Loading sections...</p>
              </div>
            ) : sections.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-400 text-lg">No sections found. Create your first section!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sections.map((section) => (
                  <div key={section.id} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="px-3 py-1 bg-primary-light text-white rounded-full text-xs font-semibold">
                              {section.section}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              (section.is_active ?? 1) === 1
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-600 text-gray-300'
                            }`}>
                              {(section.is_active ?? 1) === 1 ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold text-white mb-2">{section.title}</h3>
                          <div 
                            className="text-gray-300 text-sm line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: section.content.substring(0, 200) + '...' }}
                          />
                        </div>
                      </div>
                      <div className="flex space-x-2 mt-4">
                        <button
                          onClick={() => openViewModal(section)}
                          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors text-sm"
                        >
                          View
                        </button>
                        <button
                          onClick={() => openEditModal(section)}
                          className="px-4 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => section.id && handleDelete(section.id)}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-4xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Add New Privacy Section</h2>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="section" className="block text-white font-semibold mb-2">
                  Section ID * (e.g., section_1, section_2)
                </label>
                <input
                  type="text"
                  id="section"
                  value={formData.section}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  placeholder="section_1"
                  required
                />
              </div>

              <div>
                <label htmlFor="title" className="block text-white font-semibold mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  placeholder="1. Information We Collect"
                  required
                />
              </div>

              <div>
                <label htmlFor="content" className="block text-white font-semibold mb-2">
                  Content * (HTML supported)
                </label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => setFormData({ ...formData, content: value })}
                  placeholder="Enter section content..."
                  rows={12}
                />
              </div>

              <div>
                <label htmlFor="is_active" className="block text-white font-semibold mb-2">
                  Active Status *
                </label>
                <select
                  id="is_active"
                  value={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  required
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    saving
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-4xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Edit Privacy Section</h2>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingSection(null)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="edit-section" className="block text-white font-semibold mb-2">
                  Section ID * (e.g., section_1, section_2)
                </label>
                <input
                  type="text"
                  id="edit-section"
                  value={formData.section}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  required
                />
              </div>

              <div>
                <label htmlFor="edit-title" className="block text-white font-semibold mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  required
                />
              </div>

              <div>
                <label htmlFor="edit-content" className="block text-white font-semibold mb-2">
                  Content * (HTML supported)
                </label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => setFormData({ ...formData, content: value })}
                  placeholder="Enter section content..."
                  rows={12}
                />
              </div>

              <div>
                <label htmlFor="edit-is_active" className="block text-white font-semibold mb-2">
                  Active Status *
                </label>
                <select
                  id="edit-is_active"
                  value={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  required
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingSection(null)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    saving
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {saving ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-4xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">View Privacy Section</h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setViewingSection(null)
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Section ID</label>
                <p className="text-white font-semibold">{viewingSection.section}</p>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Title</label>
                <p className="text-white font-bold text-xl">{viewingSection.title}</p>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Content</label>
                <div className="bg-gray-700 rounded-lg p-4 text-white" dangerouslySetInnerHTML={{ __html: viewingSection.content }} />
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Active Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    (viewingSection.is_active ?? 1) === 1
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-600 text-gray-300'
                  }`}>
                    {(viewingSection.is_active ?? 1) === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {viewingSection.id && (
                  <button
                    onClick={() => handleDelete(viewingSection.id!)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    Delete
                  </button>
                )}
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    openEditModal(viewingSection)
                  }}
                  className="flex-1 px-6 py-3 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors"
                >
                  Edit Section
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setViewingSection(null)
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PrivacyPolicyManagement

